import React, {useEffect, useRef, useState} from 'react'
import {Button, Form, Modal} from 'react-bootstrap'
import {DiscountType, ICreateVoucher, IUpdateVoucher, IUserSearchResult, IVoucher} from '../../core/interfaces'
import {createVoucher, deleteVoucher, searchCreatorUsers, updateVoucher} from '../../services/vouchers'
import {DeleteConfirmationModal} from '../../core/components/DeleteConfirmationModal'
import {MessageBox} from '../../core/components/MessageBox'
import {UUID} from 'node:crypto'

interface Props {
    show: boolean
    onHide: () => void
    editVoucher?: IVoucher
    onSaved: () => void
}

interface VoucherForm {
    code: string
    discountType: DiscountType
    discountValue: string
    expireDate: string
    userId: UUID | null
    userEmail: string
    userDisplayName: string
}

const EMPTY: VoucherForm = {
    code: '',
    discountType: 'FIXED',
    discountValue: '',
    expireDate: '',
    userId: null,
    userEmail: '',
    userDisplayName: '',
}

function toLocalDatetimeValue(isoString: string): string {
    if (!isoString) return ''
    // datetime-local input expects "YYYY-MM-DDTHH:mm"
    return isoString.slice(0, 16)
}

function toIsoFromLocal(localValue: string): string {
    if (!localValue) return ''
    return new Date(localValue).toISOString()
}

export const VoucherModal: React.FC<Props> = ({show, onHide, editVoucher, onSaved}) => {
    const [form, setForm] = useState<VoucherForm>(EMPTY)
    const [userQuery, setUserQuery] = useState('')
    const [userResults, setUserResults] = useState<IUserSearchResult[]>([])
    const [isSearchingUsers, setIsSearchingUsers] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)
    const [showMessageBox, setShowMessageBox] = useState(false)
    const [message, setMessage] = useState('')
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const searchTimer = useRef<NodeJS.Timeout | null>(null)

    const isEdit = !!editVoucher

    useEffect(() => {
        if (!show) return
        if (editVoucher) {
            const isPercentage = editVoucher.discountPercentage > 0
            setForm({
                code: editVoucher.code,
                discountType: isPercentage ? 'PERCENTAGE' : 'FIXED',
                discountValue: isPercentage
                    ? String(editVoucher.discountPercentage)
                    : String(editVoucher.discount),
                expireDate: toLocalDatetimeValue(editVoucher.expireDate),
                userId: editVoucher.userId,
                userEmail: editVoucher.userEmail ?? '',
                userDisplayName: editVoucher.userDisplayName ?? '',
            })
            setUserQuery(
                editVoucher.userId
                    ? `${editVoucher.userDisplayName} (${editVoucher.userEmail})`
                    : ''
            )
        } else {
            setForm(EMPTY)
            setUserQuery('')
        }
        setUserResults([])
    }, [show, editVoucher])

    const handleField =
        (field: keyof VoucherForm) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
            setForm(prev => ({...prev, [field]: e.target.value}))
        }

    const handleUserQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const q = e.target.value
        setUserQuery(q)

        if (!q.trim()) {
            setUserResults([])
            setForm(prev => ({...prev, userId: null, userEmail: '', userDisplayName: ''}))
            return
        }

        if (searchTimer.current) clearTimeout(searchTimer.current)
        searchTimer.current = setTimeout(async () => {
            setIsSearchingUsers(true)
            try {
                const {data} = await searchCreatorUsers(q.trim())
                setUserResults(data)
            } catch {
                setUserResults([])
            } finally {
                setIsSearchingUsers(false)
            }
        }, 400)
    }

    const selectUser = (user: IUserSearchResult) => {
        setForm(prev => ({
            ...prev,
            userId: user.id,
            userEmail: user.email,
            userDisplayName: user.displayName,
        }))
        setUserQuery(`${user.displayName} (${user.email})`)
        setUserResults([])
    }

    const clearUser = () => {
        setForm(prev => ({...prev, userId: null, userEmail: '', userDisplayName: ''}))
        setUserQuery('')
        setUserResults([])
    }

    const handleSave = async () => {
        if (!form.code.trim()) {
            setIsError(true)
            setMessage('Voucher code is required.')
            setShowMessageBox(true)
            return
        }
        if (form.discountValue === '' || Number(form.discountValue) < 0) {
            setIsError(true)
            setMessage('Discount value must be 0 or greater.')
            setShowMessageBox(true)
            return
        }
        if (!form.expireDate) {
            setIsError(true)
            setMessage('Expiry date is required.')
            setShowMessageBox(true)
            return
        }

        const payload: ICreateVoucher | IUpdateVoucher = {
            code: form.code.trim(),
            discountType: form.discountType,
            discountValue: Number(form.discountValue),
            expireDate: toIsoFromLocal(form.expireDate),
            userId: form.userId,
        }

        try {
            setIsLoading(true)
            if (isEdit && editVoucher?.id) {
                await updateVoucher(editVoucher.id as string, payload)
            } else {
                await createVoucher(payload)
            }
            setIsError(false)
            setMessage(isEdit ? 'Voucher updated.' : 'Voucher created.')
            setShowMessageBox(true)
            onSaved()
            handleClose()
        } catch (e: any) {
            setIsError(true)
            setMessage(e?.message ?? 'An error occurred.')
            setShowMessageBox(true)
        } finally {
            setIsLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!editVoucher?.id) return
        try {
            await deleteVoucher(editVoucher.id as string)
            onSaved()
            handleClose()
        } catch (e: any) {
            setIsError(true)
            setMessage(e?.message ?? 'Delete failed.')
            setShowMessageBox(true)
        }
        setShowDeleteConfirm(false)
    }

    const handleClose = () => {
        setForm(EMPTY)
        setUserQuery('')
        setUserResults([])
        onHide()
    }

    return (
        <>
            <Modal show={show} onHide={handleClose} size="lg" backdrop="static" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isEdit ? `Edit: ${editVoucher?.code}` : 'New Voucher'}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form className="d-flex flex-column gap-3">
                        <Form.Group>
                            <Form.Label className="fw-bold">Code *</Form.Label>
                            <Form.Control
                                type="text"
                                value={form.code}
                                onChange={handleField('code')}
                                placeholder="e.g. SUMMER20"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label className="fw-bold">Discount Type *</Form.Label>
                            <div className="d-flex gap-4">
                                <Form.Check
                                    type="radio"
                                    label="Fixed amount (RON)"
                                    name="discountType"
                                    value="FIXED"
                                    checked={form.discountType === 'FIXED'}
                                    onChange={handleField('discountType')}
                                />
                                <Form.Check
                                    type="radio"
                                    label="Percentage (%)"
                                    name="discountType"
                                    value="PERCENTAGE"
                                    checked={form.discountType === 'PERCENTAGE'}
                                    onChange={handleField('discountType')}
                                />
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label className="fw-bold">
                                {form.discountType === 'FIXED' ? 'Discount Amount (RON) *' : 'Discount Percentage (%) *'}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                max={form.discountType === 'PERCENTAGE' ? '100' : undefined}
                                value={form.discountValue}
                                onChange={handleField('discountValue')}
                                placeholder={form.discountType === 'FIXED' ? 'e.g. 20.00' : 'e.g. 15'}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label className="fw-bold">Expiry Date *</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={form.expireDate}
                                onChange={handleField('expireDate')}
                            />
                            <Form.Text className="text-muted">
                                Setting this to a past date effectively deactivates the voucher.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label className="fw-bold">Assigned Creator (optional)</Form.Label>
                            <Form.Text className="d-block text-muted mb-1">
                                Search by name or email — only CREATOR users are shown.
                            </Form.Text>
                            <div className="position-relative">
                                <Form.Control
                                    type="text"
                                    value={userQuery}
                                    onChange={handleUserQueryChange}
                                    placeholder="Search creators by name or email..."
                                    autoComplete="off"
                                />
                                {isSearchingUsers && (
                                    <div className="position-absolute end-0 top-50 translate-middle-y pe-3">
                                        <span className="text-muted" style={{fontSize: 12}}>Searching...</span>
                                    </div>
                                )}
                                {userResults.length > 0 && (
                                    <div
                                        className="position-absolute w-100 bg-white border rounded shadow-sm"
                                        style={{zIndex: 1050, top: '100%', maxHeight: 220, overflowY: 'auto'}}
                                    >
                                        {userResults.map(u => (
                                            <div
                                                key={u.id as string}
                                                className="px-3 py-2"
                                                style={{cursor: 'pointer'}}
                                                onMouseDown={() => selectUser(u)}
                                                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                                            >
                                                <strong>{u.displayName}</strong>{' '}
                                                <span className="text-muted">— {u.email}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {form.userId && (
                                <div className="mt-2 d-flex align-items-center gap-2">
                                    <span className="badge bg-success">
                                        {form.userDisplayName} ({form.userEmail})
                                    </span>
                                    <Button
                                        size="sm"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={clearUser}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            )}
                        </Form.Group>
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    {isEdit && (
                        <Button
                            className="btn btn-danger me-auto"
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            Delete
                        </Button>
                    )}
                    <Button className="btn btn-secondary" onClick={handleClose}>Cancel</Button>
                    <Button className="btn btn-primary" onClick={handleSave} disabled={isLoading}>
                        {isLoading ? 'Saving...' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <DeleteConfirmationModal
                showDeleteConfirmationModal={showDeleteConfirm}
                setDeleteConfirmationModal={setShowDeleteConfirm}
                deleteTitle="Delete Voucher"
                deleteFunc={handleDelete}
            />

            <MessageBox
                message={message}
                setShowMessageBox={setShowMessageBox}
                showMessageBox={showMessageBox}
                isError={isError}
            />
        </>
    )
}
